// Small browser hooks for the reveal host: typing focus, reduced motion, tab
// visibility, and the floating chrome the tile stack steps aside for.
import { useEffect, useRef, useState } from "react";
import { useAnyMobileSheetOpen } from "../../../ui/components/MobileSheet";

const NON_TEXT_INPUTS = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

/** True when the element takes typed text: a text input, a textarea, contenteditable. */
export function isTypingTarget(el: Element | null | undefined): boolean {
  if (!el || typeof HTMLElement === "undefined") return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement) return !NON_TEXT_INPUTS.has(el.type) && !el.readOnly && !el.disabled;
  const html = el as HTMLElement;
  return html.isContentEditable === true || html.getAttribute?.("contenteditable") === "true";
}

/**
 * True while the member is typing somewhere (an input or textarea has focus).
 * A cinematic or a card waits until this is false.
 */
export function useTypingFocus(): boolean {
  const [typing, setTyping] = useState(() => (typeof document !== "undefined" ? isTypingTarget(document.activeElement) : false));
  useEffect(() => {
    let timer: number | undefined;
    const read = () => setTyping(isTypingTarget(document.activeElement));
    // during focusout the active element is still moving; read it once focus settles
    const onFocusOut = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(read, 0);
    };
    document.addEventListener("focusin", read);
    document.addEventListener("focusout", onFocusOut);
    read();
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("focusin", read);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);
  return typing;
}

const REDUCED_QUERY = "(prefers-reduced-motion: reduce)";

function readReducedMotion(): boolean {
  try {
    return typeof window !== "undefined" && !!window.matchMedia?.(REDUCED_QUERY).matches;
  } catch {
    return false;
  }
}

/** prefers-reduced-motion, kept live. */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(readReducedMotion);
  useEffect(() => {
    let mql: MediaQueryList | undefined;
    try {
      mql = window.matchMedia?.(REDUCED_QUERY);
    } catch {
      mql = undefined;
    }
    if (!mql) return undefined;
    const onChange = () => setReduced(readReducedMotion());
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener?.(onChange);
    return () => {
      if (mql?.removeEventListener) mql.removeEventListener("change", onChange);
      else mql?.removeListener?.(onChange);
    };
  }, []);
  return reduced;
}

/** True while the tab is visible. */
export function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(() => typeof document === "undefined" || document.visibilityState !== "hidden");
  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);
  return visible;
}

/**
 * Calls `onReturn` when the member comes back to the tab: the window regains focus
 * or the tab becomes visible. Both usually fire together, so calls within
 * `minGapMs` of each other collapse into one.
 */
export function useOnReturn(onReturn: () => void, enabled: boolean, minGapMs = 2000): void {
  const cb = useRef(onReturn);
  useEffect(() => {
    cb.current = onReturn;
  }, [onReturn]);
  useEffect(() => {
    if (!enabled) return undefined;
    let last = 0;
    const fire = () => {
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      if (now - last < minGapMs) return;
      last = now;
      cb.current();
    };
    window.addEventListener("focus", fire);
    document.addEventListener("visibilitychange", fire);
    return () => {
      window.removeEventListener("focus", fire);
      document.removeEventListener("visibilitychange", fire);
    };
  }, [enabled, minGapMs]);
}

// The site's floating chrome announces itself with window events (see
// AgentChatWidget, MobileNavBar, SitelineDock). The tile stack hides while any of
// them is open, the same way the sitebot chip does.
const CHROME_EVENTS = [
  "n450s:nav-sheet-change",
  "n450s:chat-sheet-change",
  "n450s:chat-panel-change",
  "n450s:siteline-dock-change",
] as const;

/** True while a menu, chat sheet, chat panel, siteline drawer or any bottom sheet is open. */
export function useFloatingChromeOpen(): boolean {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const sheetOpen = useAnyMobileSheetOpen();
  useEffect(() => {
    const handlers = CHROME_EVENTS.map((name) => {
      const handler = (e: Event) => {
        const isOpen = !!(e as CustomEvent<{ open?: boolean }>).detail?.open;
        setOpen((prev) => (prev[name] === isOpen ? prev : { ...prev, [name]: isOpen }));
      };
      window.addEventListener(name, handler);
      return [name, handler] as const;
    });
    return () => handlers.forEach(([name, handler]) => window.removeEventListener(name, handler));
  }, []);
  return sheetOpen || Object.values(open).some(Boolean);
}
